import React, { useState, useMemo } from 'react';
import { Drawer, Input, Button, Row, Col, Card, Typography, Space, Divider } from 'antd';
import { SearchOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import styles from '../common/DrawerModalMobile.module.css';
import { AddCategoriesModalMobile } from './AddCategoriesModalMobile';

const { Text, Title } = Typography;

interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  type: string;
}

interface CategoriesModalMobileProps {
  visible: boolean;
  onClose: () => void;
  onSelectCategory: (category: any) => void;
  onCreateNew?: () => void;
  categories?: any[];
  onCategoryCreated?: () => void;
}

const CategoriesModalMobile: React.FC<CategoriesModalMobileProps> = ({
  visible,
  onClose,
  onSelectCategory,
  onCreateNew,
  categories: externalCategories,
  onCategoryCreated
}) => {
  const [searchText, setSearchText] = useState('');
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);

  // Dữ liệu danh mục chi tiêu - sử dụng external categories hoặc fallback
  const categories: Category[] = useMemo(() => {
    console.log('CategoriesModalMobile - External categories:', externalCategories);
    if (externalCategories && externalCategories.length > 0) {
      // Chuyển đổi dữ liệu từ API sang format phù hợp
      const mappedCategories = externalCategories.map(cat => ({
        id: cat.id.toString(),
        name: cat.name,
        icon: cat.icon,
        color: cat.color,
        type: cat.type || 'living' // fallback type
      }));
      console.log('CategoriesModalMobile - Mapped categories:', mappedCategories);
      return mappedCategories;
    }
    
    // Fallback data
    return [
      // Chi tiêu - sinh hoạt
      { id: '1', name: 'Chợ, siêu thị', icon: '🛒', color: '#FF6B35', type: 'living' },
      { id: '2', name: 'Ăn uống', icon: '🍽️', color: '#FF6B35', type: 'living' },
      { id: '3', name: 'Di chuyển', icon: '🚗', color: '#4A90E2', type: 'living' },
      
      // Chi phí phát sinh
      { id: '4', name: 'Mua sắm', icon: '🛍️', color: '#FF6B35', type: 'incidental' },
      { id: '5', name: 'Giải trí', icon: '▶️', color: '#FF4757', type: 'incidental' },
      { id: '6', name: 'Làm đẹp', icon: '💄', color: '#FF69B4', type: 'incidental' },
      { id: '7', name: 'Sức khỏe', icon: '❤️', color: '#FF4757', type: 'incidental' },
      { id: '8', name: 'Từ thiện', icon: '💝', color: '#FF69B4', type: 'incidental' },
      
      // Chi phí cố định
      { id: '9', name: 'Hóa đơn', icon: '📄', color: '#2ED573', type: 'fixed' },
      { id: '10', name: 'Nhà cửa', icon: '🏠', color: '#A55EEA', type: 'fixed' },
      { id: '11', name: 'Người thân', icon: '👶', color: '#FF69B4', type: 'fixed' },
      
      // Đầu tư - tiết kiệm
      { id: '12', name: 'Đầu tư', icon: '🌱', color: '#7ED321', type: 'investment' },
      { id: '13', name: 'Học tập', icon: '📚', color: '#A55EEA', type: 'investment' },
    ];
  }, [externalCategories]);

  // Phân loại danh mục
  const categoryGroups = {
    living: {
      title: 'Chi tiêu - sinh hoạt',
      icon: '📋',
      color: '#FF6B35',
      columns: 3
    },
    incidental: {
      title: 'Chi phí phát sinh',
      icon: '🏠',
      color: '#FFD93D',
      columns: 4
    },
    fixed: {
      title: 'Chi phí cố định',
      icon: '🏠',
      color: '#FFA500',
      columns: 3
    },
    investment: {
      title: 'Đầu tư - tiết kiệm',
      icon: '🌱',
      color: '#7ED321',
      columns: 2
    }
  };

  // Lọc danh mục theo search
  const filteredCategories = useMemo(() => {
    if (!searchText) return categories;
    return categories.filter(category =>
      category.name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [searchText, categories]);

  // Nhóm danh mục theo type
  const groupedCategories = useMemo(() => {
    const groups: Record<string, Category[]> = {};
    filteredCategories.forEach(category => {
      if (!groups[category.type]) {
        groups[category.type] = [];
      }
      groups[category.type].push(category);
    });
    return groups;
  }, [filteredCategories]);

  const handleCategorySelect = (category: Category) => {
    console.log('CategoriesModalMobile - Selected category:', category);
    onSelectCategory(category);
    onClose();
  };

  const handleCreateNew = () => {
    setShowAddCategoryModal(true);
  };

  const handleCategoryCreated = () => {
    setShowAddCategoryModal(false);
    onCategoryCreated?.();
  };

  return (
    <Drawer
      title={null}
      placement="bottom"
      onClose={onClose}
      open={visible}
      height="85vh"
      className={styles.categoryDrawer}
      bodyStyle={{ padding: 0 }}
      headerStyle={{ display: 'none' }}
      closeIcon={<CloseOutlined />}
    >
      <div className={styles.categoryDrawerBody}>
        {/* Header */}
        <div className={styles.categoryModalHeader}>
          <Title level={4} className={styles.categoryModalTitle}>
            Chọn danh mục
          </Title>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onClose}
            className={styles.categoryModalCloseButton}
          />
        </div>

        {/* Search and Create New */}
        <div className={styles.categoryModalSearchSection}>
          <Input
            placeholder="Tìm kiếm"
            prefix={<SearchOutlined style={{ color: '#999' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className={styles.categoryModalSearchInput}
          />
          <Button
            type="default"
            icon={<PlusOutlined />}
            onClick={handleCreateNew}
            className={styles.categoryModalCreateButton}
          >
            Tạo mới
          </Button>
        </div>

        {/* Categories Content */}
        <div className={styles.categoryModalContent}>
          {Object.entries(groupedCategories).map(([type, categoryList]) => {
            const groupInfo = categoryGroups[type as keyof typeof categoryGroups];
            if (!groupInfo) return null;

                          return (
                <div key={type} className={styles.categoryGroup}>
                  {/* Group Header */}
                  <div className={styles.categoryGroupHeader}>
                    <span 
                      className={styles.categoryGroupIcon}
                      style={{ color: groupInfo.color }}
                    >
                      {groupInfo.icon}
                    </span>
                    <Text 
                      strong 
                      className={styles.categoryGroupTitle}
                      style={{ color: groupInfo.color }}
                    >
                      {groupInfo.title}
                    </Text>
                  </div>

                  {/* Categories Grid */}
                  <Row gutter={[12, 12]} style={{ marginBottom: '16px' }}>
                    {categoryList.map((category) => (
                      <Col key={category.id} span={24 / groupInfo.columns}>
                        <Card
                          hoverable
                          className={styles.categoryCard}
                          bodyStyle={{ padding: '12px 8px' }}
                          onClick={() => handleCategorySelect(category)}
                        >
                          <div className={styles.categoryCardBody}>
                            <div 
                              className={styles.categoryIcon}
                              style={{ color: category.color }}
                            >
                              {category.icon}
                            </div>
                            <Text className={styles.categoryName}>
                              {category.name}
                            </Text>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              );
          })}

          {Object.keys(groupedCategories).length === 0 && (
            <div className={styles.categoryEmptyState}>
              <Text>Không tìm thấy danh mục phù hợp</Text>
            </div>
          )}
        </div>
      </div>

      {/* Add Category Modal */}
      <AddCategoriesModalMobile
        isOpen={showAddCategoryModal}
        onClose={() => setShowAddCategoryModal(false)}
        onSuccess={handleCategoryCreated}
      />
    </Drawer>
  );
};

export default CategoriesModalMobile;
